import { useState, useLayoutEffect, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Drawer } from "@mui/material";
import styled from "styled-components";
import support_icon from "@/assets/images/question.png";
import setting_icon from "@/assets/images/setting.png";
import back_icon from "@/assets/images/back.png";
import menu_icon from "@/assets/images/hamburger-menu.png";
import dashboard_icon from "@/assets/images/dashboard/dashboard.png";
import contact_icon from "@/assets/images/dashboard/contacts.png";
import invoice_icon from "@/assets/images/dashboard/invoices.png";
import get_paid_icon from "@/assets/images/dashboard/get-paid.svg";
import contract_icon from "@/assets/images/dashboard/contracts.png";
import integration_icon from "@/assets/images/dashboard/integrations.png";
import payment_icon from "@/assets/images/dashboard/payment-methods.png";
import transaction_icon from "@/assets/images/dashboard/transactions.png";
import receipt_text_icon from "@/assets/images/dashboard/receipt-text.png";
import {
  SidebarLabel,
  SidebarToolSection,
  StepIcon,
  StepSection,
  MobileNavWrapper,
  MobileNavHeader,
  MobileNavSection,
  DrawerSection,
  // SidebarMainNav,
} from "./styled";
import { useAuth } from "@/providers/auth";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { usePlatformData } from "@/hooks/platform/use-platform-data";
import { useSettings } from "@/hooks/setting/use-settings";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { usePayableInvoices } from "@/hooks/invoice/use-payable-invoices";
import {
  PAST_DUE_INVOICE_STATUS,
  UNPAID_AND_OVERDUE_INVOICE_STATUS,
  UNPAID_INVOICE_STATUS,
} from "@/constants/invoice";

import stellaLogoFull from "@/assets/images/stella-logo-full-white.svg";
import stellaLogoShort from "@/assets/images/stella-logo-short-white.svg";

const useRouteAccess = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [canAccess, setCanAccess] = useState(true);

  useEffect(() => {
    const role = user?.cognito_user_info?.UserRole;
    if (
      role === "admin" &&
      [
        "/bills",
        "/bills/create",
        // "/payment-methods",
        "/plaid",
        "/settings",
      ].includes(location.pathname)
    ) {
      setCanAccess(false);
    }
  }, [user?.cognito_user_info?.UserRole]);

  return canAccess;
};

const useNavigationList = (tenancy) => {
  const { user } = useAuth();
  const isNewNav = tenancy?.feature_flags?.new_nav;
  const role = user?.cognito_user_info?.UserRole;

  const navigation = useMemo(() => {
    const base = [
      { name: "Dashboard", value: "dashboard", icon: dashboard_icon },
      { name: "Contacts", value: "contacts", icon: contact_icon },
      ...(isNewNav
        ? [
            { name: "Get Paid", value: "invoices", icon: get_paid_icon },
            { name: "Pay Bills", value: "pay-bills", icon: invoice_icon },
          ]
        : [{ name: "Invoices", value: "invoices", icon: invoice_icon }]),
      // { name: "Quotes", value: "quotes", icon: contract_icon },
      // { name: "Integrations", value: "integrations", icon: integration_icon },
      { name: "Move Money", value: "move-money", icon: payment_icon },
      { name: "Transactions", value: "transactions", icon: transaction_icon },
      // {
      //   name: "Bills",
      //   value: "bills",
      //   icon: receipt_text_icon,
      // },
    ];

    if (role === "admin") {
      return base.filter(
        (item) => !["bills", "settings"].includes(item.value)
      );
    }
    return base;
  }, [isNewNav, role]);

  return navigation;
};

const SidebarWrapper = styled.div`
  width: 100%;
  max-width: 314px;
  min-height: 800px;
  background-color: ${(props) => props.color};
  padding: 38px;
  position: fixed;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-height: 800px) {
    min-height: 0px;
  }
`;

const Sidebar = ({ currentWidth, setCurrentWidth }) => {
  const history = useHistory();
  const { signout, user } = useAuth();
  const canAccess = useRouteAccess();
  const { data: platformData } = usePlatformData();
  const { data: settings } = useSettings();
  const { data: tenancy } = useTenancy();
  const navigation = useNavigationList(tenancy);
  const { data: payables } = usePayableInvoices();

  const payablesCount = useMemo(() => {
    return payables?.reduce((acc, payable) => {
      if (
        [
          UNPAID_INVOICE_STATUS,
          PAST_DUE_INVOICE_STATUS,
          UNPAID_AND_OVERDUE_INVOICE_STATUS,
        ].includes(payable.invoice_info?.invoice_status?.toLowerCase())
      ) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [payables]);

  const primaryColor = user?.platform_settings?.primary_color || "#000";
  const logo = settings?.logo || user?.platform_settings?.logo || stellaLogoFull;
  const logoUrl =
    logo === stellaLogoFull
      ? stellaLogoFull
      : `https://cdn.pocketbook.tech/${logo}`;
  const [drawer, setDrawer] = useState({
    right: false,
  });
  const [logoError, setLogoError] = useState(false);

  const currentUrl = window.location.href;
  const currentPath = currentUrl.substring(currentUrl.lastIndexOf("/") + 1);

  if (!canAccess) {
    history.replace("/");
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useLayoutEffect(() => {
    handleResize();
  }, []);

  function handleResize() {
    const width = window.innerWidth;
    setCurrentWidth(width);
  }

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setDrawer({ ...drawer, [anchor]: open });
  };

  const handleRedirect = (path) => {
    history.push(`/${path}`);
  };

  const handleLogout = async () => {
    await signout({
      onSuccess: (data) => {
        if (!data.is_authenticated) {
          history.push("/login");
        }
      },
      onError: (error) => {
        toast.error("Signout failed. Please try again in a moment.");
      },
    });
  };

  return (
    <>
      {currentWidth > 768 ? (
        <SidebarWrapper color={primaryColor}>
          <div>
            {platformData?.commercial_logo_full ||
            platformData?.commercial_logo ? (
              <LogoImage
                onClick={() => handleRedirect("dashboard")}
                src={logoUrl}
                alt="dba-logo"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div
                role="status"
                class="space-y-8 animate-pulse md:space-y-0 md:space-x-8 rtl:space-x-reverse md:flex md:items-center"
              >
                <div class="flex items-center justify-center w-full h-16 bg-gray-300 rounded-lg sm:w-96 dark:bg-gray-700">
                  <svg
                    class="w-10 h-10 text-gray-200 dark:text-gray-600"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 18"
                  >
                    <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                  </svg>
                </div>
              </div>
            )}
          </div>
          <StepSection className="align-items-start">
            {navigation.map((item) => (
              <SidebarLabel
                key={item.value}
                className={currentPath === item.value && "opacity-10"}
                onClick={() => handleRedirect(item.value)}
              >
                <StepIcon src={item.icon} alt={item.value} />
                <span className="pl-25">{item.name}</span>
                {item.value === "pay-bills" && payablesCount > 0 && (
                  <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white">
                    {payablesCount}
                  </span>
                )}
              </SidebarLabel>
            ))}
          </StepSection>
          <div style={{ flexGrow: 1 }}></div>
          <SidebarToolSection>
            {user?.cognito_user_info?.UserRole !== "admin" && (
              <SidebarLabel
                className={currentPath === "settings" && "opacity-10"}
                onClick={() => handleRedirect("settings")}
              >
                <StepIcon src={setting_icon} alt="setting" />
                <span className="pl-25">Settings</span>
              </SidebarLabel>
            )}

            <SidebarLabel
              className={currentPath === "support" && "opacity-10"}
              onClick={() => handleRedirect("support")}
            >
              <StepIcon src={support_icon} alt="support" />
              <span className="pl-25">Support</span>
            </SidebarLabel>
            <SidebarLabel onClick={handleLogout}>
              <StepIcon src={back_icon} alt="back" />
              <span className="pl-25">Logout</span>
            </SidebarLabel>
          </SidebarToolSection>
        </SidebarWrapper>
      ) : (
        <MobileNavWrapper style={{ backgroundColor: primaryColor }}>
          <MobileNavHeader>
            {platformData?.commercial_logo_full ||
            platformData?.commercial_logo ? (
              <img
                onClick={() => handleRedirect("dashboard")}
                src={logoUrl}
                alt="dba-logo"
                className="h-[32px] w-auto"
              />
            ) : (
              <div
                role="status"
                class="space-y-8 animate-pulse md:space-y-0 md:space-x-8 rtl:space-x-reverse md:flex md:items-center"
              >
                <div class="flex items-center justify-center w-24 h-10 bg-gray-300 rounded-lg sm:w-96 dark:bg-gray-700">
                  <svg
                    class="w-6 h-6 text-gray-200 dark:text-gray-600"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 18"
                  >
                    <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                  </svg>
                </div>
              </div>
            )}
            <img
              src={menu_icon}
              alt="menu"
              className="cursor-pointer w-[25px] h-auto"
              onClick={toggleDrawer("right", true)}
            />
          </MobileNavHeader>
          <Drawer
            anchor={"right"}
            open={drawer["right"]}
            onClose={toggleDrawer("right", false)}
            PaperProps={{
              sx: {
                backgroundColor: primaryColor,
              },
            }}
          >
            <DrawerSection>
              <MobileNavSection className="align-items-start">
                {navigation.map((item) => (
                  <SidebarLabel
                    key={item.value}
                    className={currentPath === item.value && "opacity-10"}
                    onClick={() => handleRedirect(item.value)}
                  >
                    <StepIcon src={item.icon} alt={item.value} />
                    <span className="pl-25">{item.name}</span>
                    {item.value === "pay-bills" && payablesCount > 0 && (
                      <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white">
                        {payablesCount}
                      </span>
                    )}
                  </SidebarLabel>
                ))}
              </MobileNavSection>
              <div style={{ flexGrow: 1 }}></div>
              <SidebarToolSection>
                {user?.cognito_user_info?.UserRole === "owner" && (
                  <SidebarLabel
                    className={currentPath === "settings" && "opacity-10"}
                    onClick={() => handleRedirect("settings")}
                  >
                    <StepIcon src={setting_icon} alt="setting" />
                    <span className="pl-25">Settings</span>
                  </SidebarLabel>
                )}
                <SidebarLabel
                  className={currentPath === "support" && "opacity-10"}
                  onClick={() => handleRedirect("support")}
                >
                  <StepIcon src={support_icon} alt="support" />
                  <span className="pl-25">Support</span>
                </SidebarLabel>
                <SidebarLabel onClick={handleLogout}>
                  <StepIcon src={back_icon} alt="back" />
                  <span className="pl-25">Logout</span>
                </SidebarLabel>
              </SidebarToolSection>
            </DrawerSection>
          </Drawer>
        </MobileNavWrapper>
      )}
    </>
  );
};

const LogoImage = styled.img`
  max-height: 70px;
  width: auto;
  object-fit: contain;
`;

export default Sidebar;
